/**
 * The two readouts the star's own cards are drawn from: what a gamble would pay, and what became
 * of the player who took the star.
 *
 * Pure and DOM-free, which is `vote-card.ts`' shape and for its reason — the arithmetic on a card
 * is exactly the thing to assert, and the card it is drawn on cannot be laid out in jsdom at all.
 *
 * **THE ONE NUMBER HERE IS THE WHOLE OF WHY THIS MODULE EXISTS.** A star-taker has ten seconds to
 * choose between an ordinary winner's share of the pot and three times their own mass with their
 * run on the line, and they are choosing it while being chased. Asking somebody to multiply their
 * own mass by three under that is asking them to get it wrong: what the card prints is the actual
 * figure, off the mass the HUD is already holding.
 */

import { STAR_GAMBLE_PAYOUT_MULTIPLIER } from '@chompdome/shared/constants';
import type { StarOutcome } from '@chompdome/shared/types';

/**
 * What calling it right would pay, in mass.
 *
 * **It is `STAR_GAMBLE_PAYOUT_MULTIPLIER` times the taker's OWN mass, which is the simulation's
 * own arithmetic and not a second copy of it** — the multiplier is imported rather than written
 * down here, exactly as `rocket-pack.ts` imports the rocket's numbers, so the figure on the card
 * cannot drift from the figure the room pays. Sized off the taker rather than off a winner's
 * share for the reason `STAR_GAMBLE_PAYOUT_MULTIPLIER` argues at length: the stake is the whole
 * run either way, so the prize has to be measured in the same thing that is being risked, or the
 * gamble is dominated by the auto-win at every size and the best decision in the feature stops
 * being a decision.
 *
 * **It is an upper bound and the card has to say so.** The payout is capped at what the pot
 * actually holds — mass never comes out of nothing — so in a small or a one-sided room a correct
 * call pays less than this. Printing it as a promise would be the one number on the card that
 * could be a lie; printing it with "or the pot, whichever is smaller" beside it is the honest
 * shape, and it is what `StarChoiceCard` draws.
 *
 * Rounded and floored at zero for reading rather than for arithmetic, which is the treatment
 * every other mass in the HUD gets: nothing downstream computes with this.
 */
export function starGamblePayout(mass: number): number {
  if (!Number.isFinite(mass) || mass <= 0) return 0;
  return Math.round(mass * STAR_GAMBLE_PAYOUT_MULTIPLIER);
}

/**
 * What the room is told became of the star-taker, for the results card.
 *
 * `VoteResultSnapshot.starOutcome` is room-wide rather than per-viewer — the whole room watched
 * the star go up, so what happened to the player who took it is the room's business — and this is
 * the sentence for it. It deliberately does **not** name them: the frame carries no id and no
 * name for the taker, and the `star_taken` notice already named them to everybody when they
 * picked it up.
 *
 * `lost` says the run ended rather than "they died", because that is what actually happened: a
 * wrong call ends the run down `leave`'s path with a null killer, so nobody ate them and nothing
 * grew from it.
 */
export function starOutcomeLine(outcome: StarOutcome): string {
  switch (outcome) {
    case 'auto':
      return 'The star-taker skipped the call and took a winner’s share.';
    case 'won':
      return 'The star-taker called it, and the room agreed. They took the pot.';
    case 'lost':
      return 'The star-taker called it wrong. Their run ended there.';
  }
}
